import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Mail, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Merci ! Vous êtes inscrit à notre newsletter.');
      setEmail('');
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <footer className="bg-surface pt-20 pb-8" data-testid="footer">
      <div className="container-custom">
        {/* Newsletter Section */}
        <div className="text-center mb-16 pb-16 border-b border-border">
          <h3 className="font-serif text-3xl md:text-4xl text-text-primary mb-4">
            Rejoignez l'univers Secret Musc
          </h3>
          <p className="text-text-secondary mb-8 max-w-md mx-auto">
            Inscrivez-vous pour recevoir nos nouveautés et offres exclusives
          </p>
          <form 
            onSubmit={handleNewsletterSubmit}
            className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto"
            data-testid="newsletter-form"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Votre email"
              className="input-elegant flex-1"
              data-testid="newsletter-email"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary whitespace-nowrap"
              data-testid="newsletter-submit"
            >
              {isLoading ? 'Envoi...' : "S'inscrire"}
            </button>
          </form>
        </div>

        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_beauty-essence-15/artifacts/eqbnhryg_Logo%20Secret%20Musc.PNG" 
                alt="Secret Musc" 
                className="h-14 w-auto object-contain"
              />
            </Link>
            <p className="text-text-secondary text-sm leading-relaxed">
              L'instant douceur. Des parfums intimes et soins corporels d'exception, 
              créés avec amour en France.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-serif text-lg text-text-primary mb-6">Navigation</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="footer-link text-sm">Accueil</Link>
              </li>
              <li>
                <Link to="/products" className="footer-link text-sm">Boutique</Link>
              </li>
              <li>
                <Link to="/#about" className="footer-link text-sm">Notre Histoire</Link>
              </li>
              <li>
                <Link to="/#contact" className="footer-link text-sm">Contact</Link>
              </li>
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-serif text-lg text-text-primary mb-6">Catégories</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/products?category=brumes" className="footer-link text-sm">
                  Brumes Corporelles
                </Link>
              </li>
              <li>
                <Link to="/products?category=cremes" className="footer-link text-sm">
                  Crèmes pour la Peau
                </Link>
              </li>
              <li>
                <Link to="/products?category=musc" className="footer-link text-sm">
                  Musc & Parfums
                </Link>
              </li>
              <li>
                <Link to="/products?category=barbes" className="footer-link text-sm">
                  Produits Barbes
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg text-text-primary mb-6">Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <a href="mailto:contact@secretmusc.com" className="text-text-secondary text-sm hover:text-accent transition-colors">contact@secretmusc.com</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <a href="https://wa.me/32494368029" target="_blank" rel="noopener noreferrer" className="text-text-secondary text-sm hover:text-accent transition-colors">+32 4 94 36 80 29</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                <a href="https://secretmusc.com" target="_blank" rel="noopener noreferrer" className="text-text-secondary text-sm hover:text-accent transition-colors">secretmusc.com</a>
              </li>
            </ul>
            {/* Social Links */}
            <div className="flex gap-4 mt-6">
              <a 
                href="https://instagram.com/Secretmusc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white border border-border flex items-center justify-center hover:border-accent hover:text-accent transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white border border-border flex items-center justify-center hover:border-accent hover:text-accent transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center pt-8 border-t border-border">
          <p className="text-text-secondary text-sm">
            © {new Date().getFullYear()} Secret Musc. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
